import { useEffect } from 'react';

/**
 * Asks iOS to keep the screen awake while the WebClip is open.
 *
 * **Best-effort, and explicitly not a fix for SPEC §14 #7.** The app still stops
 * dead the moment the screen goes off; this only makes the screen less likely to
 * go off on its own. The provisioning steps in SPEC §10 — Auto-Lock: Never, Low
 * Power Mode off, plugged in, Guided Access — remain the actual requirement, and
 * a device set up without them will still go quiet.
 *
 * Worth having anyway: it costs a dozen lines and covers the case where somebody
 * re-images a handset and forgets one Settings toggle.
 *
 * The lock is released by the OS whenever the page is hidden, so it has to be
 * re-acquired on every return to the foreground — hence the `visibilitychange`
 * listener rather than a one-shot request.
 */
export function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const wakeLock = (navigator as Navigator & { wakeLock?: WakeLockNavigator }).wakeLock;
    if (!wakeLock) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      try {
        sentinel = await wakeLock.request('screen');
      } catch {
        // Denied (Low Power Mode, an unsupported build, a page that lost focus
        // mid-request). Nothing to do and nothing to report — the manual device
        // settings are the real guarantee.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel?.release().catch(() => {});
    };
  }, [enabled]);
}

interface WakeLockSentinelLike {
  release(): Promise<void>;
}

interface WakeLockNavigator {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}
